import React, { useEffect, useState } from "react";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "";

const PERIODS = [
  { key: "day", label: "24H" },
  { key: "week", label: "7D" },
  { key: "month", label: "30D" },
];

const ROWS = [
  { key: "totalTrades", category: "all", label: "Total Trades", accent: "blue" },
  { key: "tpHit", category: "tp", label: "TP Hit", accent: "green" },
  { key: "slHit", category: "sl", label: "SL Hit", accent: "red" },
];

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

function formatTradeTime(value) {
  const dt = new Date(value);
  if (isNaN(dt)) return "—";
  const datePart = dt.toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
  let hours = dt.getHours();
  const minutes = String(dt.getMinutes()).padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12;
  if (hours === 0) hours = 12;
  return `${datePart}, ${hours}:${minutes} ${ampm}`;
}

export default function SpikeStatsCard() {
  const [presetStats, setPresetStats] = useState(null);
  const [period, setPeriod] = useState("day");
  const [category, setCategory] = useState("all");
  const [trades, setTrades] = useState(null);

  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState(todayIso());
  const [range, setRange] = useState(null); // { from, to } once applied, else null (preset mode)
  const [rangeStats, setRangeStats] = useState(null);

  useEffect(() => {
    let cancelled = false;

    fetch(`${API_BASE_URL}/api/public/spike-bridge-stats`)
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => {
        if (!cancelled && data) {
          setPresetStats(data);
        }
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!range) {
      setRangeStats(null);
      return undefined;
    }

    let cancelled = false;

    fetch(`${API_BASE_URL}/api/public/spike-bridge-stats?from=${range.from}&to=${range.to}`)
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => {
        if (!cancelled && data) {
          setRangeStats(data);
        }
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [range]);

  useEffect(() => {
    let cancelled = false;
    setTrades(null);

    const query = range
      ? `category=${category}&from=${range.from}&to=${range.to}`
      : `category=${category}&period=${period}`;

    fetch(`${API_BASE_URL}/api/public/spike-bridge-trades?${query}`)
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => {
        if (!cancelled && data) {
          setTrades(data.items || []);
        }
      })
      .catch(() => {
        if (!cancelled) setTrades([]);
      });

    return () => {
      cancelled = true;
    };
  }, [category, period, range]);

  function selectPeriod(key) {
    setRange(null);
    setPeriod(key);
  }

  function applyRange() {
    if (!fromDate || !toDate) return;
    setRange({ from: fromDate, to: toDate });
  }

  const activeRow = ROWS.find((row) => row.category === category);
  const values = range ? rangeStats : presetStats;
  const rowValue = (key) => (range ? values?.[key] : values?.[key]?.[period]) ?? (values ? 0 : "—");

  return (
    <div className="landing-stats-card">
      <div className="landing-stats-card-header">
        <span className="landing-signal-label">Live Signal Results</span>
        <div className="landing-stats-tabs">
          {PERIODS.map((p) => (
            <button
              key={p.key}
              type="button"
              className={`landing-stats-tab${!range && period === p.key ? " landing-stats-tab-active" : ""}`}
              onClick={() => selectPeriod(p.key)}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      <div className="landing-stats-daterange">
        <input
          type="date"
          className="landing-stats-date-input"
          value={fromDate}
          max={toDate || undefined}
          onChange={(event) => setFromDate(event.target.value)}
          aria-label="From date"
        />
        <span className="landing-stats-date-sep">to</span>
        <input
          type="date"
          className="landing-stats-date-input"
          value={toDate}
          min={fromDate || undefined}
          max={todayIso()}
          onChange={(event) => setToDate(event.target.value)}
          aria-label="To date"
        />
        <button
          type="button"
          className="landing-stats-date-apply"
          onClick={applyRange}
          disabled={!fromDate || !toDate}
        >
          Apply
        </button>
      </div>

      <div className="landing-stats-rows">
        {ROWS.map((row) => (
          <button
            key={row.key}
            type="button"
            className={`landing-stats-row landing-stats-row-clickable${category === row.category ? " landing-stats-row-selected" : ""}`}
            onClick={() => setCategory(row.category)}
          >
            <span className={`landing-stats-dot landing-stats-dot-${row.accent}`} aria-hidden="true" />
            <span className="landing-stats-row-label">{row.label}</span>
            <span className="landing-stats-row-value">{rowValue(row.key)}</span>
          </button>
        ))}
      </div>

      <div className="landing-stats-table-wrap">
        <div className="landing-stats-table-title">{activeRow?.label} · Pair / Side / Price / Time</div>
        {trades === null ? (
          <div className="landing-stats-table-empty">Loading…</div>
        ) : trades.length === 0 ? (
          <div className="landing-stats-table-empty">No trades in this period</div>
        ) : (
          <div className="landing-stats-table">
            {trades.map((trade, index) => (
              <div className="landing-stats-table-row" key={`${trade.symbol}-${trade.created_at}-${index}`}>
                <span className="landing-stats-table-pair">{trade.symbol}</span>
                <span className={`landing-stats-table-side landing-stats-table-side-${String(trade.side).toLowerCase()}`}>
                  {trade.side}
                </span>
                <span className="landing-stats-table-price">{Number(trade.price).toFixed(2)}</span>
                <span className="landing-stats-table-time">{formatTradeTime(trade.created_at)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
